import React, { useState, useEffect, useMemo, useSyncExternalStore } from 'react';
import { Box, Typography, Slider, Grid, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import audioEngine from '../utils/audioEngine';
import {
  REVERB_DECAY,
  REVERB_DECAY_KEY,
  REVERB_MIX,
  REVERB_MIX_KEY,
  DEGREE_VOLUMES_KEY,
  DEGREE_DEFAULTS_DB,
  DEGREE_MIN_DB,
  dbToGain,
} from '../utils/audioSettings';
import { useUserSetting } from '../data/hooks/useUserSetting';
import { subscribeUser, getUserVersion } from '../data/userStore';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: 'rgba(30, 30, 47, 0.7)',
  borderRadius: '8px',
  marginBottom: theme.spacing(3),
}));

// Chord degrees exposed in the note mixer. Les dB par défaut, le plancher et la
// conversion vivent dans audioSettings : le démarrage lit exactement les mêmes
// valeurs, sans attendre que cette page soit montée.
const DEGREES = [
  { key: 'drone', label: 'Drone (bass)' },
  { key: 'root', label: 'Root' },
  { key: 'third', label: 'Third' },
  { key: 'fifth', label: 'Fifth' },
  { key: 'seventh', label: 'Seventh' },
];
const DEGREE_MAX_DB = 6;

const AudioMixer = ({ embedded = false }) => {
  // Audio parameters read from the user mirror, synced.
  const [reverbMix, setReverbMix] = useUserSetting(REVERB_MIX_KEY, REVERB_MIX);
  // La durée est commune à toute l'app depuis le passage en bus d'envoi, donc
  // une seule valeur de référence (cf reverbBus). Un compte qui avait déjà
  // enregistré sa propre durée la garde.
  const [reverbDecay, setReverbDecay] = useUserSetting(REVERB_DECAY_KEY, REVERB_DECAY);
  // Chord instrument selection (default WAV drone / piano / guitar / lush layer).
  // Lecture live, jamais figee au montage : sur Android le timbre par defaut
  // depend du verdict de puissance, qui n'est rendu qu'a la fin du prechargement.
  // Un etat capture avant ce verdict affichait un timbre que le moteur ne jouait
  // plus (constate sur l'appareil). L'abonnement au mirror redeclenche le rendu
  // quand le verdict ou le choix utilisateur change.
  useSyncExternalStore(subscribeUser, getUserVersion, getUserVersion);
  const instrument = audioEngine.getInstrument();
  // Per-degree volumes in dB. Merge stored values over the defaults so a missing
  // degree falls back to its default dB.
  const [storedDegreeVolumes, setDegreeVolumes] = useUserSetting(DEGREE_VOLUMES_KEY, DEGREE_DEFAULTS_DB);
  const degreeVolumes = useMemo(() => DEGREES.reduce((acc, d) => {
    acc[d.key] = storedDegreeVolumes[d.key] !== undefined ? storedDegreeVolumes[d.key] : DEGREE_DEFAULTS_DB[d.key];
    return acc;
  }, {}), [storedDegreeVolumes]);

  // Live values while a slider is being dragged. Audio is applied directly on
  // each change; the setting (persistence + global emit) is only written on
  // onChangeCommitted so dragging a fader never re-renders the whole app.
  const [live, setLive] = useState({});
  const liveOr = (key, persisted) => (live[key] !== undefined ? live[key] : persisted);
  const setLiveValue = (key, value) => setLive(prev => ({ ...prev, [key]: value }));
  const clearLiveValue = (key) => setLive(prev => {
    const next = { ...prev };
    delete next[key];
    return next;
  });

  // Apply per-degree volumes to the audio engine (persistence handled by setSetting)
  useEffect(() => {
    DEGREES.forEach(({ key }) => {
      audioEngine.setDegreeVolume(key, dbToGain(degreeVolumes[key]));
    });
  }, [degreeVolumes]);

  const handleDegreeVolumeChange = (key, newValue) => {
    setLiveValue(`degree:${key}`, newValue);
    audioEngine.setDegreeVolume(key, dbToGain(newValue));
  };

  const handleDegreeVolumeCommitted = (key, newValue) => {
    setDegreeVolumes({ ...degreeVolumes, [key]: newValue });
    clearLiveValue(`degree:${key}`);
  };

  // Update reverb settings when they change
  useEffect(() => {
    audioEngine.setReverbMix(reverbMix);
    audioEngine.setReverbDecay(reverbDecay);
  }, [reverbMix, reverbDecay]);

  const handleReverbMixChange = (event, newValue) => {
    setLiveValue('reverbMix', newValue);
    audioEngine.setReverbMix(newValue);
  };

  const handleReverbMixCommitted = (event, newValue) => {
    setReverbMix(newValue);
    clearLiveValue('reverbMix');
  };

  const handleReverbDecayChange = (event, newValue) => {
    setLiveValue('reverbDecay', newValue);
    audioEngine.setReverbDecay(newValue);
  };

  const handleReverbDecayCommitted = (event, newValue) => {
    setReverbDecay(newValue);
    clearLiveValue('reverbDecay');
  };

  const handleInstrumentChange = (name) => {
    audioEngine.setInstrument(name);
  };
  
  const Frame = embedded ? Box : StyledPaper;
  const frameProps = embedded ? {} : { elevation: 3 };

  return (
    <Box className="audio-mixer" sx={{ width: '100%' }}>
      <Frame {...frameProps}>
        {!embedded && (
          <Typography variant="h6" gutterBottom>
            Audio Mixer
          </Typography>
        )}

        <Grid container spacing={3}>
          {/* Chord instrument */}
          <Grid item xs={12}>
            <Typography gutterBottom>
              Chord sound
            </Typography>
            <Grid container spacing={1}>
              {/* Internal keys are unchanged ('lush' = drone+piano, 'default' = bare WAV drone);
                  only the user-facing labels and order changed: lush -> "Default", default -> "Pad". */}
              {[['lush', 'Default'], ['default', 'Pad'], ['piano', 'Piano'], ['guitar', 'Guitar']].map(([val, label]) => (
                <Grid item xs={6} sm={3} key={val}>
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={() => handleInstrumentChange(val)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      color: instrument === val ? '#0a0a0c' : 'white',
                      backgroundColor: instrument === val ? 'var(--accent)' : 'rgba(255, 255, 255, 0.08)',
                      '&:hover': {
                        backgroundColor: instrument === val ? 'var(--accent)' : 'rgba(255, 255, 255, 0.12)'
                      }
                    }}
                  >
                    {label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Reverb Mix */}
          <Grid item xs={12} md={6}>
            <Typography id="reverb-mix-slider" gutterBottom>
              Reverb Mix
            </Typography>
            <Slider
              value={liveOr('reverbMix', reverbMix)}
              onChange={handleReverbMixChange}
              onChangeCommitted={handleReverbMixCommitted}
              aria-labelledby="reverb-mix-slider"
              min={0}
              max={1}
              step={0.01}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            />
          </Grid>
          
          {/* Reverb Decay */}
          <Grid item xs={12} md={6}>
            <Typography id="reverb-decay-slider" gutterBottom>
              Reverb Decay
            </Typography>
            <Slider
              value={liveOr('reverbDecay', reverbDecay)}
              onChange={handleReverbDecayChange}
              onChangeCommitted={handleReverbDecayCommitted}
              aria-labelledby="reverb-decay-slider"
              min={0.1}
              max={10}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value.toFixed(1)}s`}
            />
          </Grid>

          {/* Note Mixer - per-degree volumes */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>
              Note Mixer
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
              Volume of the drone and of each chord tone. The seventh only affects 7th chords.
            </Typography>
            <Grid container spacing={3}>
              {DEGREES.map(({ key, label }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Typography id={`degree-${key}-slider`} gutterBottom>
                    {label}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <VolumeDown />
                    </Grid>
                    <Grid item xs>
                      <Slider
                        value={liveOr(`degree:${key}`, degreeVolumes[key])}
                        onChange={(e, newValue) => handleDegreeVolumeChange(key, newValue)}
                        onChangeCommitted={(e, newValue) => handleDegreeVolumeCommitted(key, newValue)}
                        aria-labelledby={`degree-${key}-slider`}
                        min={DEGREE_MIN_DB}
                        max={DEGREE_MAX_DB}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => (value <= DEGREE_MIN_DB ? 'Muted' : `${value} dB`)}
                      />
                    </Grid>
                    <Grid item>
                      <VolumeUp />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Frame>
    </Box>
  );
};

export default AudioMixer;
